/**
 * Camada de imagem do OpenLayers cuja imagem (já decodificada) é trocada
 * instantaneamente a cada quadro do loop.
 *
 * Por que não `ImageStatic`? Trocar a fonte faria o OL baixar/decodificar a
 * imagem de novo, e durante interações (arrastar/zoom) o renderizador padrão
 * não troca a imagem — o loop "congelaria" ao arrastar. Aqui:
 * - `FrameSource` guarda um `ImageWrapper` já no estado LOADED;
 * - `FrameLayerRenderer` sempre desenha a imagem atual da fonte, mesmo
 *   durante interações, sem nunca mostrar um quadro vazio entre dois quadros.
 */
import ImageWrapper from 'ol/Image';
import ImageState from 'ol/ImageState';
import ImageLayer from 'ol/layer/Image';
import type { FrameState } from 'ol/Map';
import CanvasImageLayerRenderer from 'ol/renderer/canvas/ImageLayer';
import ImageSource from 'ol/source/Image';
import type { AttributionLike } from 'ol/source/Source';
import { getHeight, getWidth, type Extent } from 'ol/extent';
import type { FrameImage } from '../goes/frameCache';
import { GOES_PROJ } from './projection';

export class FrameSource extends ImageSource {
  private readonly defaultExtent: Extent;
  private wrapper: ImageWrapper | null = null;
  private current: FrameImage | null = null;

  constructor(extent: Extent, attributions?: AttributionLike) {
    super({ projection: GOES_PROJ, attributions, interpolate: true });
    this.defaultExtent = extent;
  }

  /**
   * Exibe a imagem (já decodificada) na extensão dada — a extensão exata do
   * produto/tamanho (ver imageGeometry.ts). `null` limpa a camada.
   */
  setFrameImage(image: FrameImage | null, extent: Extent = this.defaultExtent): void {
    if (image === this.current && (!image || this.wrapper?.getExtent() === extent)) return;
    this.current = image;
    if (image) {
      const wrapper = new ImageWrapper(
        extent,
        [getWidth(extent) / image.width, getHeight(extent) / image.height],
        1,
        ImageState.LOADED,
      );
      wrapper.setImage(image);
      this.wrapper = wrapper;
    } else {
      this.wrapper = null;
    }
    this.changed();
  }

  getFrameImage(): FrameImage | null {
    return this.current;
  }

  getWrapper(): ImageWrapper | null {
    return this.wrapper;
  }

  override getImageInternal(): ImageWrapper {
    return this.wrapper as ImageWrapper;
  }
}

export class FrameLayerRenderer extends CanvasImageLayerRenderer {
  override prepareFrame(_frameState: FrameState): boolean {
    const source = this.getLayer().getSource() as FrameSource | null;
    this.image = source?.getWrapper() ?? null;
    return !!this.image;
  }

  /**
   * Suavização de alta qualidade ao desenhar: reduzir uma imagem de 7200 px
   * para a tela (linhas da NOAA sem serrilhado) e ampliar no zoom máximo
   * (sem aspecto "quadriculado").
   */
  protected override preRender(
    context: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
    frameState: FrameState,
  ): void {
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    super.preRender(context, frameState);
  }
}

export class FrameImageLayer extends ImageLayer<FrameSource> {
  protected override createRenderer(): CanvasImageLayerRenderer {
    return new FrameLayerRenderer(this);
  }
}
